import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ListItem } from '../../types/list';
import { BaseDetailCard } from '../shared/BaseDetailCard';
import { AppStoreIcon, GooglePlayIcon } from '../icons';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { useSection } from '../../hooks/useSection';
import { useAutoTranslation } from '../../hooks/useAutoTranslation';
import { useImageDisplay } from '../../hooks/useImageDisplay';
import { logger } from '../../lib/logger';
import { favoritesService } from '../../lib/favorites/favoritesService';

interface DetailCardProps {
  item: ListItem;
  items: ListItem[];
  onClose: () => void;
  onItemChange: (item: ListItem) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  tableId: string;
  categoryColors: Map<string, string>;
}

export const DetailCard: React.FC<DetailCardProps> = ({
  item,
  items,
  onClose,
  onItemChange,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  tableId,
  categoryColors
}) => {
  const { i18n } = useTranslation();
  const section = useSection();
  const currentLanguage = i18n.language;
  const [isFavorite, setIsFavorite] = useState(() => favoritesService.isFavorite(item.id));
  
  // Use the useImageDisplay hook to determine if image should be displayed
  const { displayImage, shouldDisplay } = useImageDisplay(item.image, 'detailCard');

  // Utiliser le hook useAutoTranslation avec le sectionType
  const { translate, translating, error: translationError } = useAutoTranslation({
    description: item.description,
    currentLanguage,
    context: {
      tableId,
      itemId: item.id,
      sectionType: 'liste'
    }
  });

  // Effectuer la traduction quand la langue change
  useEffect(() => {
    const checkAndTranslate = async () => {
      const currentLang = i18n.language;
      
      // Vérifier si la traduction est manquante ou vide
      const currentDescription = item.descriptions[currentLang];
      const isDescriptionMissing = !currentDescription || 
                                 currentDescription.trim() === '' || 
                                 currentDescription === '\n';

      if (isDescriptionMissing) {
        const translated = await translate();
        if (translated) {
          // Mettre à jour les descriptions de l'item
          item.descriptions[currentLang] = translated;
          
          logger.debug('Translation completed for list item', {
            category: 'Translation',
            data: { 
              itemId: item.id,
              itemName: item.name,
              language: currentLang,
              translatedLength: translated.length
            }
          });
        }
      }
    };

    checkAndTranslate();
  }, [item.descriptions, i18n.language, translate, item.id, item.name]);

  const getTranslatedText = (key: string): string => {
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  const renderContent = () => null;

  const renderBookingButton = () => {
    if (!item.appstore && !item.playstore) {
      return null;
    }

    return (
      <div className="flex flex-col gap-2 mb-4">
        {item.appstore && (
          <a
            href={item.appstore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analyticsService.trackItemAction(
                section.id,
                item.id,
                'app_store_click',
                'list',
                {
                  item_name: item.name,
                  category: item.category,
                  type: item.type,
                  url: item.appstore,
                  section_name: section.names.fr || 'Unknown Section',
                  section_type: 'liste'
                }
              );
            }}
            className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: config['major-color'] }}
          >
            <AppStoreIcon size={18} className="mr-2" />
            {getTranslatedText('btn_app_store')}
          </a>
        )}
        {item.playstore && (
          <a
            href={item.playstore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              analyticsService.trackItemAction(
                section.id,
                item.id,
                'play_store_click',
                'list',
                {
                  item_name: item.name,
                  category: item.category,
                  type: item.type,
                  url: item.playstore,
                  section_name: section.names.fr || 'Unknown Section',
                  section_type: 'liste'
                }
              );
            }}
            className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
            style={{ backgroundColor: config['major-color'] }}
          >
            <GooglePlayIcon size={18} className="mr-2" />
            {getTranslatedText('btn_play_store')}
          </a>
        )}
      </div>
    );
  };

  const handleFavoriteToggle = async (newState: boolean) => {
    if (newState) {
      favoritesService.addFavorite({
        id: item.id,
        sourceType: 'liste',
        data: item,
        metadata: {
          sourceSection: {
            id: section.id,
            type_rubrique: 'liste',
            names: section.names
          },
          sectionSlug: section.names.fr,
          sectionNames: section.names,
          item_id: item.id,
          section_id: section.id
        }
      });
      
      analyticsService.trackItemAction(
        section.id,
        item.id,
        'add_favorite',
        'list',
        {
          item_name: item.name,
          category: item.category,
          type: item.type,
          section_name: section.names.fr || 'Unknown Section',
          section_type: 'liste'
        }
      );
    } else {
      favoritesService.removeFavorite(item.id);
      
      analyticsService.trackItemAction(
        section.id,
        item.id,
        'remove_favorite',
        'list',
        {
          item_name: item.name,
          category: item.category,
          type: item.type,
          section_name: section.names.fr || 'Unknown Section',
          section_type: 'liste'
        }
      );
    }
    setIsFavorite(newState);
  };

  // Obtenir la description à afficher
  const getDescription = () => {
    // Si une traduction existe déjà dans les descriptions, l'utiliser
    if (item.descriptions[currentLanguage]) {
      return item.descriptions[currentLanguage];
    }
    
    // Fallback sur la description par défaut
    return item.description;
  };

  return (
    <BaseDetailCard
      item={item}
      items={items}
      onClose={onClose}
      onItemChange={onItemChange}
      config={config}
      translations={translations}
      tableId={tableId}
      renderContent={renderContent}
      renderBookingButton={renderBookingButton}
      onCategorySelect={onCategorySelect}
      onTypeSelect={onTypeSelect}
      categoryColors={categoryColors}
      getTranslatedCategory={getTranslatedCategory}
      getTranslatedText={getTranslatedText}
      context={{
        sectionType: 'liste',
        itemId: item.id,
        section_id: section.id
      }}
      isFavorite={isFavorite}
      onFavoriteToggle={handleFavoriteToggle}
      image={shouldDisplay ? displayImage : undefined}
      description={getDescription()}
      translating={translating}
      translationError={translationError}
    />
  );
};

export default DetailCard;